import { Fragment, useCallback, useEffect, useMemo } from 'react';
import { Combobox, DropdownSelect, FormField, FormHelp, Label } from '@zillow/constellation-10';
import usePropertyFilter from '../../../../hooks/usePropertyFilter';
import { IsVisible } from '@zg-rentals/react-use';
import { SkeletonListItem } from '../../../LeadManagementPropertySelector/Skeleton';

type PropertySelectorProps = {
  listingAlias: string;
  onPropertySelect: (listingAlias: string) => void;
};

export default function PropertySelector({ listingAlias, onPropertySelect }: PropertySelectorProps) {
  const updatePathWithListingAlias = useCallback(
    (newListingAlias: string) => {
      onPropertySelect(newListingAlias);
    },
    [onPropertySelect],
  );

  const {
    defaultListingAddress,
    errorMessage,
    handleScroll,
    handleSelectProperty,
    hasMoreToPaginate,
    isUrlListingInLoadedOptions,
    propertyListings,
    shouldShowSkeleton,
  } = usePropertyFilter({
    defaultListingAliasOnLoad: listingAlias,
    updatePathWithListingAlias,
  });

  // Auto-select first property when list loads and nothing is selected yet
  useEffect(() => {
    if (propertyListings.length > 0 && !listingAlias) {
      onPropertySelect(propertyListings[0].value);
    }
  }, [propertyListings, listingAlias, onPropertySelect]);

  const options = useMemo(() => {
    const shouldPrependUrlListingOption = listingAlias && !isUrlListingInLoadedOptions && defaultListingAddress;

    if (!shouldPrependUrlListingOption) {
      return propertyListings;
    }

    const syntheticOption = {
      value: listingAlias,
      label: defaultListingAddress,
    };
    const otherOptions = propertyListings.filter((o) => o.value !== listingAlias);
    return [syntheticOption, ...otherOptions];
  }, [listingAlias, isUrlListingInLoadedOptions, defaultListingAddress, propertyListings]);

  if (shouldShowSkeleton) {
    return (
      <FormField
        label={<Label>Select a property</Label>}
        control={<DropdownSelect disabled options={[]} placeholder="Loading properties..." css={{ width: '100%' }} />}
      />
    );
  }

  return (
    <FormField
      data-testid="screening-criteria-property-selector"
      label={<Label>Select a property</Label>}
      error={!!errorMessage}
      description={errorMessage ? <FormHelp>{errorMessage}</FormHelp> : undefined}
      control={
        <DropdownSelect
          options={options}
          value={listingAlias}
          onOptionSelect={handleSelectProperty}
          placeholder="Select a property"
          showLabelForValue
          css={{ width: '100%', boxShadowColor: 'border.subtle' }}
          renderOption={({
            option,
            highlightPattern,
            key,
            ref,
            renderOptionLabel,
            renderOptionMeta,
            renderOptionMedia: _renderOptionMedia,
            ...rest
          }) => {
            if (typeof option !== 'object') return null;

            const { value, label, meta } = option;
            const isLastItemInList =
              propertyListings.length > 0 && value === propertyListings[propertyListings.length - 1].value;

            return (
              <Fragment key={key}>
                <Combobox.Option option={option} ref={typeof ref === 'string' ? undefined : ref} {...rest}>
                  {renderOptionLabel?.({ children: label, highlightPattern })}
                  {meta && renderOptionMeta?.({ children: meta, highlightPattern })}
                </Combobox.Option>

                {isLastItemInList && hasMoreToPaginate && (
                  <IsVisible onChange={handleScroll} options={{ rootMargin: '0px 0px 200px 0px' }}>
                    {({ ref }) => <SkeletonListItem ref={ref} />}
                  </IsVisible>
                )}
              </Fragment>
            );
          }}
        />
      }
    />
  );
}
